import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import type { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { autopopulateCampaignData, checkCampaignExists } from "src/services/EngagementAPIs";
import { getApiLangFromI18n } from "src/utils/apiLang";
import type { AudienceListOption } from "src/utils/audienceListLabels";
import type {
  AutopopulateResponse,
  CampaignChannel,
  CampaignData,
} from "../../EngagementInterfaces";
import {
  AI_EMAIL_EDITOR_ROUTE,
  AUDIENCE_SEPARATOR,
  SITEMAP_MESSAGE_DURATION_SECONDS,
  SITEMAP_MESSAGE_KEY,
  TEMPLATE_EMAIL_EDITOR_ROUTE,
  WHATSAPP_CAMPAIGN_EDITOR_ROUTE,
} from "./CampaignSetupConstants";
import { getUnsubscribedLists, isSitemapAvailable } from "./CampaignSetupUtils";
import { useAudienceListsAndCompany } from "./UseAudienceListsAndCompany";

const getStoredCredentials = () => {
  const tenantId = localStorage.getItem("tenant_id");
  const apiKey = localStorage.getItem("api_key");
  return tenantId && apiKey ? { tenantId, apiKey } : null;
};

const splitAudience = (audience: string): string[] =>
  audience
    .split(AUDIENCE_SEPARATOR)
    .map(item => item.trim())
    .filter(Boolean);

const getEditorRoute = (channel: CampaignChannel, isAIGeneratorOn: boolean) => {
  if (channel === "whatsapp") {
    return WHATSAPP_CAMPAIGN_EDITOR_ROUTE;
  }
  return isAIGeneratorOn ? AI_EMAIL_EDITOR_ROUTE : TEMPLATE_EMAIL_EDITOR_ROUTE;
};

interface CampaignSetupPreset {
  channel?: CampaignChannel;
  useAi?: boolean;
  listName?: string;
}

export const useCampaignSetup = (originalCampaignId?: string, preset?: CampaignSetupPreset) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [channel, setChannel] = useState<CampaignChannel | null>(preset?.channel ?? null);
  const [campaignName, setCampaignName] = useState("");
  const [sendTo, setSendTo] = useState<string[]>(preset?.listName ? [preset.listName] : []);
  const [excludeList, setExcludeList] = useState<string[]>([]);
  const handleOptionsLoaded = useCallback(
    (options: AudienceListOption[]) =>
      setExcludeList(current => (current.length ? current : getUnsubscribedLists(options))),
    []
  );
  const { audienceOptions, companyName, isListsLoading } =
    useAudienceListsAndCompany(handleOptionsLoaded);
  const [emailHtml, setEmailHtml] = useState("");
  const [isAutopopulating, setIsAutopopulating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAIGeneratorOn, setIsAIGeneratorOn] = useState(preset?.useAi ?? true);
  const [isAIGeneratorDisabled, setIsAIGeneratorDisabled] = useState(false);

  useEffect(() => {
    let isActive = true;

    const checkSitemapAvailability = async () => {
      const isAvailable = await isSitemapAvailable(getApiLangFromI18n(i18n));
      if (isAvailable || !isActive) {
        return;
      }
      setIsAIGeneratorDisabled(true);
      setIsAIGeneratorOn(false);
      message.open({
        key: SITEMAP_MESSAGE_KEY,
        type: "info",
        content: i18n.t("campaignFlow.createCampaignNew.aiDisableMessage"),
        duration: SITEMAP_MESSAGE_DURATION_SECONDS,
      });
    };

    void checkSitemapAvailability();

    return () => {
      isActive = false;
    };
  }, [i18n]);

  useEffect(() => {
    const credentials = getStoredCredentials();
    if (!originalCampaignId || !credentials) {
      return;
    }

    let isActive = true;

    const autopopulate = async () => {
      setIsAutopopulating(true);
      try {
        const data: AutopopulateResponse = await autopopulateCampaignData(
          Number(credentials.tenantId),
          originalCampaignId,
          credentials.apiKey
        );
        if (!isActive || data.status !== "success") {
          return;
        }

        setCampaignName(data.campaign_name);
        setSendTo(splitAudience(data.audience));
        if (data.dont_end_to_audience) {
          setExcludeList(splitAudience(data.dont_end_to_audience));
        }
        setEmailHtml(data.email_html);
      } catch {
        if (isActive) {
          message.error(i18n.t("campaignFlow.createCampaignNew.autopopulateFailed"));
        }
      } finally {
        if (isActive) {
          setIsAutopopulating(false);
        }
      }
    };

    autopopulate();

    return () => {
      isActive = false;
    };
  }, [originalCampaignId, i18n]);

  const excludeOptions = useMemo(
    () => audienceOptions.filter(option => !sendTo.includes(option.name)),
    [audienceOptions, sendTo]
  );

  const handleAIGeneratorChange = useCallback(
    (checked: boolean) => {
      if (!isAIGeneratorDisabled) {
        setIsAIGeneratorOn(checked);
      }
    },
    [isAIGeneratorDisabled]
  );

  const isBusy = isAutopopulating || isSubmitting;
  const canSubmit =
    !isBusy &&
    Boolean(channel) &&
    Boolean(campaignName.trim()) &&
    Boolean(companyName.trim()) &&
    sendTo.length > 0;

  const handleSubmit = useCallback(async () => {
    const trimmedCampaignName = campaignName.trim();
    const trimmedCompanyName = companyName.trim();
    const buildCampaignData = (selectedChannel: CampaignChannel): CampaignData => ({
      channel: selectedChannel,
      campaignName,
      sendTo,
      excludeList,
      originalCampaignId,
      scheduledDateTime: null,
      emailHtml: emailHtml || undefined,
      companyNameEngagements: trimmedCompanyName,
    });

    if (!channel) {
      message.error(t("campaignFlow.createCampaignNew.selectChannel"));
      return;
    }
    if (!trimmedCampaignName) {
      message.error(t("campaignFlow.createCampaignNew.enterCampaignName"));
      return;
    }
    if (!trimmedCompanyName) {
      message.error(t("campaignFlow.createCampaignNew.companyNameRequired"));
      return;
    }
    if (!sendTo.length) {
      message.error(t("campaignFlow.createCampaignNew.selectSendTo"));
      return;
    }

    setIsSubmitting(true);
    try {
      await checkCampaignExists({
        tenant_id: Number(localStorage.getItem("tenant_id") || 0),
        campaign_name: trimmedCampaignName,
      });

      navigate(getEditorRoute(channel, isAIGeneratorOn), {
        state: { campaignData: buildCampaignData(channel) },
      });
    } catch (error) {
      const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      message.error(detail || t("campaignFlow.createCampaignNew.campaignExists"));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    campaignName,
    channel,
    companyName,
    emailHtml,
    excludeList,
    isAIGeneratorOn,
    navigate,
    originalCampaignId,
    sendTo,
    t,
  ]);

  return {
    channel,
    campaignName,
    companyName,
    sendTo,
    excludeList,
    audienceOptions,
    excludeOptions,
    isListsLoading,
    canSubmit,
    isAIGeneratorOn,
    isAIGeneratorDisabled,
    setChannel,
    setCampaignName,
    setSendTo,
    setExcludeList,
    handleAIGeneratorChange,
    handleSubmit,
  };
};
